/**
 * workspace.tsx — /#/workspace
 *
 * W66.X7 — Rebuilt per Bryan K. Short's locked doctrine 2026-06-03.
 *
 * WORKSPACE = the AE's command center for the entire business. Annual + quarterly
 * position, pipeline coverage, activity statistics (real effort signal), book health
 * across all 10 CPs, where to focus this week. Drill-down hub to every other surface.
 *
 * Layout (top to bottom):
 *   1. ANNUAL COMMAND HEADER — 5-tile position + quarter breakdown
 *   2. PIPELINE COVERAGE RAIL — book value, weighted, 5x target, visual bar
 *   3. ACTIVITY STATISTICS STRIP — 6 cards, this week vs last week
 *   4. BOOK HEALTH HEAT-MAP — accounts × CP1..CP10, color by answer strength
 *   5. FOCUS THIS WEEK — 3-5 auto-derived accounts with one-line rationale
 *   6. JUMPSTART RAMP TILE — shown only if hire_date within last 90 days (deferred if column missing)
 *   7. DRILL-DOWN GRID — 6 buttons: Today · Command View · Sales Plan · Tower · Helix · Galaxy
 *
 * Canon:
 *   No exclamation points. No emojis. M-PACT hyphenated.
 *   5-color palette: #6B7280, #DC2626, #EA580C, #EAB308, #16A34A.
 *   Bryan K. Short. Demo AE 1 (never Geoff).
 *   Tenant 1 only.
 *   No First Hour. No Cadence Queue.
 *   AE name is Demo AE 1.
 */

import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { getSession, api } from "@/lib/session";
import OperatorShell from "@/components/operator/OperatorShell";
import { ColorLegendFooter } from "@/components/ColorLegendFooter";

// ── Brand constants ────────────────────────────────────────────────────────────
const BG        = "#F9FAFB";
const CARD      = "#FFFFFF";
const BORDER    = "#E5E7EB";
const MI6_RED   = "#DC2626";
const ORANGE    = "#EA580C";
const YELLOW    = "#EAB308";
const GREEN     = "#16A34A";
const GRAY      = "#6B7280";
const TEXT_DARK = "#111827";
const TEXT_MID  = "#374151";
const TEXT_MUTE = "#6B7280";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt$(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function coverageColor(ratio: number): string {
  if (ratio >= 5) return GREEN;
  if (ratio >= 3) return YELLOW;
  return MI6_RED;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface GapMathData {
  has_quota: boolean;
  annual_quota: number;
  quarter_quota: number;
  closed_ytd: number;
  commit: number;
  best_case: number;
  gap: number;
  weighted_pipeline: number;
  coverage_ratio: number;
  days_remaining: number;
  pace_pct: number;
  coach_cue: string;
}

interface Top10Account {
  rank: number;
  account_id: number;
  account_name: string;
  composite_score: number;
  next_cp: number | null;
  next_cp_label: string | null;
  days_stalled: number | null;
  forecast_bucket: string;
}

interface AccountCpStatus {
  account_id: number;
  account_name: string;
  cp_number: number;
  status: "green" | "amber" | "red" | null;
  answer_strength?: "Strong" | "Qualified" | "Developing" | "Early" | null;
}

interface ActivityStats {
  writes_this_week: number;
  writes_last_week: number;
  days_active: number;
  accounts_touched: number;
  cp_advances: number;
  contacts_added: number;
  answers_logged: number;
}

interface W50Payload {
  top_10_accounts: Top10Account[];
  meta: {
    fetched_at: string;
    today: string;
    yesterday: string;
    role: string;
    version: string;
  };
}

interface BookHealthData {
  account_id: number;
  account_name: string;
  cp_statuses: Record<number, { status: "green" | "amber" | "red" | null; strength?: string | null }>;
}

// ── 1. ANNUAL COMMAND HEADER ──────────────────────────────────────────────────
function AnnualCommandHeader({
  gapMath,
  loading,
}: {
  gapMath: GapMathData | null;
  loading: boolean;
}) {
  const year = new Date().getFullYear();
  const quarter = Math.ceil((new Date().getMonth() + 1) / 3);

  if (loading) {
    return (
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
        <div className="h-16 rounded animate-pulse" style={{ background: BORDER }} />
      </div>
    );
  }

  if (!gapMath?.has_quota) {
    return (
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
        <p style={{ fontSize: 13, color: TEXT_MUTE }}>No quota plan on file. Contact your manager.</p>
      </div>
    );
  }

  const annual   = gapMath.annual_quota;
  const closed   = gapMath.closed_ytd;
  const gap      = gapMath.gap;
  const weighted = gapMath.weighted_pipeline;
  const coverage = gapMath.coverage_ratio;

  // Quarter breakdown (estimated from YTD — actual quarterly splits not yet in API)
  const qClosed  = closed; // Full YTD as current quarter proxy — refine when quarterly endpoint ships
  const qForecast = weighted;

  const tiles = [
    { label: `Annual Quota — ${year}`, value: fmt$(annual), color: TEXT_DARK },
    { label: "Closed YTD", value: fmt$(closed), color: closed > 0 ? GREEN : TEXT_DARK },
    { label: "Gap to Annual", value: fmt$(gap), color: gap > 0 ? MI6_RED : GREEN },
    { label: "Weighted Pipeline", value: fmt$(weighted), color: TEXT_DARK },
    {
      label: "Pipeline Coverage",
      value: `${coverage.toFixed(1)}x`,
      sub: "Rule of thumb: 5x",
      color: coverageColor(coverage),
    },
  ];

  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
      {/* 5-tile header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {tiles.map((t) => (
          <div key={t.label}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                color: TEXT_MUTE,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              {t.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: t.color, lineHeight: 1.1 }}>
              {t.value}
            </div>
            {t.sub && (
              <div style={{ fontSize: 10, color: TEXT_MUTE, marginTop: 2 }}>{t.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* Quarter breakdown */}
      <div
        style={{
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 12,
          display: "flex",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 11, color: TEXT_MUTE }}>
          <span style={{ fontWeight: 600, color: TEXT_MID }}>Q{quarter} YTD closed: </span>
          <span style={{ color: closed > 0 ? GREEN : TEXT_MUTE }}>{fmt$(closed)}</span>
        </div>
        <div style={{ fontSize: 11, color: TEXT_MUTE }}>
          <span style={{ fontWeight: 600, color: TEXT_MID }}>Q{quarter} weighted pipe: </span>
          <span>{fmt$(weighted)}</span>
        </div>
        <div style={{ fontSize: 11, color: TEXT_MUTE }}>
          <span style={{ fontWeight: 600, color: TEXT_MID }}>Remaining gap: </span>
          <span style={{ color: gap > 0 ? MI6_RED : GREEN }}>{fmt$(gap)}</span>
        </div>
        <div style={{ fontSize: 11, color: TEXT_MUTE }}>
          <span style={{ fontWeight: 600, color: TEXT_MID }}>5x coverage target: </span>
          <span>{fmt$(gap * 5)}</span>
        </div>
      </div>
    </div>
  );
}

// ── 2. PIPELINE COVERAGE RAIL ─────────────────────────────────────────────────
function PipelineCoverageRail({ gapMath, loading }: { gapMath: GapMathData | null; loading: boolean }) {
  if (loading) {
    return (
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
        <div className="h-16 rounded animate-pulse" style={{ background: BORDER }} />
      </div>
    );
  }
  if (!gapMath?.has_quota) return null;

  const { annual_quota, gap, weighted_pipeline, coverage_ratio } = gapMath;
  const target5x = gap * 5;
  // Fill bar: 0 to 5x coverage
  const fillPct  = Math.min(100, (coverage_ratio / 5) * 100);
  const barColor = coverageColor(coverage_ratio);

  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
          marginBottom: 16,
        }}
      >
        Pipeline Coverage
      </div>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: TEXT_MUTE, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 2 }}>
            Open Pipeline
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK }}>{fmt$(weighted_pipeline)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: TEXT_MUTE, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 2 }}>
            5x Target
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK }}>{fmt$(target5x)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: TEXT_MUTE, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 2 }}>
            Current Multiple
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: barColor }}>{coverage_ratio.toFixed(1)}x</div>
        </div>
      </div>

      {/* Visual bar 0–5x */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: TEXT_MUTE, marginBottom: 4 }}>
          <span>0x</span>
          <span style={{ color: TEXT_MID }}>1x</span>
          <span style={{ color: TEXT_MID }}>2x</span>
          <span style={{ color: YELLOW, fontWeight: 700 }}>3x</span>
          <span style={{ color: TEXT_MID }}>4x</span>
          <span style={{ color: GREEN, fontWeight: 700 }}>5x (target)</span>
        </div>
        <div
          style={{
            height: 10,
            background: BORDER,
            borderRadius: 5,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${fillPct}%`,
              background: barColor,
              borderRadius: 5,
              transition: "width 0.6s ease",
            }}
          />
        </div>
        <div style={{ fontSize: 10, color: TEXT_MUTE, marginTop: 4 }}>
          Red below 3x. Yellow 3–4.9x. Green at or above 5x.
        </div>
      </div>
    </div>
  );
}

// ── 3. ACTIVITY STATISTICS STRIP ──────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string;
  delta?: string;
  deltaPositive?: boolean;
}

function StatCard({ label, value, delta, deltaPositive }: StatCardProps) {
  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: "14px 16px",
        flex: "1 1 140px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: TEXT_MUTE,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: TEXT_DARK }}>{value}</div>
      {delta && (
        <div
          style={{
            fontSize: 11,
            marginTop: 2,
            color: deltaPositive ? GREEN : MI6_RED,
          }}
        >
          {delta}
        </div>
      )}
    </div>
  );
}

function ActivityStrip({
  stats,
  loading,
}: {
  stats: ActivityStats | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="animate-pulse rounded"
            style={{ background: CARD, border: `1px solid ${BORDER}`, flex: "1 1 140px", height: 80 }}
          />
        ))}
      </div>
    );
  }

  const s = stats;
  const writesThisWeek = s?.writes_this_week ?? 0;
  const writesLastWeek = s?.writes_last_week ?? 0;
  const writesDelta    = writesThisWeek - writesLastWeek;

  const cards: StatCardProps[] = [
    {
      label: "Writes",
      value: `${writesThisWeek}`,
      delta: writesDelta !== 0 ? `${writesDelta > 0 ? "+" : ""}${writesDelta} vs last week` : undefined,
      deltaPositive: writesDelta >= 0,
    },
    {
      label: "Days Active",
      value: `${s?.days_active ?? 0} / 5`,
    },
    {
      label: "Accounts Touched",
      value: `${s?.accounts_touched ?? 0} of 10`,
    },
    {
      label: "CP Advances",
      value: `${s?.cp_advances ?? 0}`,
    },
    {
      label: "Contacts Added",
      value: `${s?.contacts_added ?? 0}`,
    },
    {
      label: "CP Answers",
      value: `${s?.answers_logged ?? 0}`,
    },
  ];

  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        Activity — This Week
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </div>
    </div>
  );
}

// ── 4. BOOK HEALTH HEAT-MAP ───────────────────────────────────────────────────
function strengthColor(strength?: string | null, status?: string | null): string {
  if (strength === "Strong" || status === "green") return GREEN;
  if (strength === "Qualified") return YELLOW;
  if (strength === "Developing" || status === "amber") return ORANGE;
  if (strength === "Early" || status === "red") return MI6_RED;
  return BORDER; // unanswered / gray
}

function BookHealthHeatMap({
  accounts,
  loading,
}: {
  accounts: Top10Account[];
  loading: boolean;
}) {
  // We use composite_score and next_cp as a proxy for book health per account.
  // A dedicated /api/workspace/book-health endpoint would provide per-CP status;
  // that endpoint is not yet registered. We render what we have and document the gap.
  const cpNums = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  if (loading) {
    return (
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
        <div className="h-40 rounded animate-pulse" style={{ background: BORDER }} />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
        <p style={{ fontSize: 13, color: TEXT_MUTE }}>No Top-10 accounts on file.</p>
      </div>
    );
  }

  function cellColor(account: Top10Account, cp: number): string {
    const nextCp = account.next_cp ?? 1;
    const score  = account.composite_score ?? 0;
    if (cp < nextCp) {
      // Cleared floors — color by score band
      if (score >= 80) return GREEN;
      if (score >= 60) return YELLOW;
      if (score >= 40) return ORANGE;
      return MI6_RED;
    }
    if (cp === nextCp) return YELLOW; // current floor
    return BORDER; // pending
  }

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        overflow: "auto",
      }}
    >
      <div
        style={{
          padding: "14px 20px 10px",
          borderBottom: `1px solid ${BORDER}`,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
        }}
      >
        Book Health
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: BG }}>
              <th
                style={{
                  textAlign: "left",
                  padding: "6px 12px",
                  color: TEXT_MUTE,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${BORDER}`,
                  whiteSpace: "nowrap",
                  minWidth: 160,
                }}
              >
                Account
              </th>
              {cpNums.map((cp) => (
                <th
                  key={cp}
                  style={{
                    textAlign: "center",
                    padding: "6px 6px",
                    color: TEXT_MUTE,
                    fontWeight: 700,
                    borderBottom: `1px solid ${BORDER}`,
                    minWidth: 36,
                  }}
                >
                  CP{cp}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.account_id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                <td
                  style={{
                    padding: "8px 12px",
                    color: TEXT_DARK,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 200,
                  }}
                >
                  {a.account_name}
                </td>
                {cpNums.map((cp) => (
                  <td key={cp} style={{ textAlign: "center", padding: "6px 6px" }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        background: cellColor(a, cp),
                        margin: "0 auto",
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ padding: "8px 16px", fontSize: 10, color: TEXT_MUTE, display: "flex", gap: 12 }}>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: GREEN,
              borderRadius: 2,
              marginRight: 4,
            }}
          />
          Strong
        </span>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: YELLOW,
              borderRadius: 2,
              marginRight: 4,
            }}
          />
          Qualified / Current
        </span>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: ORANGE,
              borderRadius: 2,
              marginRight: 4,
            }}
          />
          Developing
        </span>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: MI6_RED,
              borderRadius: 2,
              marginRight: 4,
            }}
          />
          Early
        </span>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: BORDER,
              borderRadius: 2,
              marginRight: 4,
            }}
          />
          Not reached
        </span>
      </div>
    </div>
  );
}

// ── 5. FOCUS THIS WEEK ────────────────────────────────────────────────────────
interface FocusItem {
  account_name: string;
  account_id: number;
  rationale: string;
}

function deriveFocusItems(accounts: Top10Account[]): FocusItem[] {
  const items: FocusItem[] = [];

  // Sort by days stalled descending
  const sorted = [...accounts].sort((a, b) => (b.days_stalled ?? 0) - (a.days_stalled ?? 0));

  for (const a of sorted) {
    if (items.length >= 5) break;
    const days = a.days_stalled ?? 0;
    if (days >= 14) {
      items.push({
        account_name: a.account_name,
        account_id: a.account_id,
        rationale: `${days} days without CP progression. CP${a.next_cp ?? "?"} stalled. Action: determine next intelligence move or bow out.`,
      });
    } else if (a.composite_score < 30 && a.composite_score > 0) {
      items.push({
        account_name: a.account_name,
        account_id: a.account_id,
        rationale: `Scoring below 30. Book is thin on this account — push to gather intelligence or reassess fit.`,
      });
    }
  }

  if (items.length === 0 && accounts.length > 0) {
    const a = accounts[0];
    items.push({
      account_name: a.account_name,
      account_id: a.account_id,
      rationale: `Top-ranked account. Confirm all CP evidence is current and contact coverage is complete.`,
    });
  }

  return items;
}

function FocusThisWeek({
  accounts,
  loading,
}: {
  accounts: Top10Account[];
  loading: boolean;
}) {
  const [, setLocation] = useLocation();
  const items = loading ? [] : deriveFocusItems(accounts);

  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 20 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        Focus This Week
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 rounded animate-pulse" style={{ background: BORDER }} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 13, color: TEXT_MUTE }}>
          Your book is current. No accounts flagged for urgent focus.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item, i) => (
            <div
              key={item.account_id}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                paddingBottom: i < items.length - 1 ? 10 : 0,
                borderBottom: i < items.length - 1 ? `1px solid ${BORDER}` : "none",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: MI6_RED,
                  marginTop: 6,
                }}
              />
              <div>
                <button
                  onClick={() => setLocation(`/account/${item.account_id}`)}
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: MI6_RED,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    textDecoration: "underline",
                    marginBottom: 2,
                  }}
                >
                  {item.account_name}
                </button>
                <p style={{ fontSize: 12, color: TEXT_MID, margin: 0 }}>{item.rationale}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 7. DRILL-DOWN GRID ────────────────────────────────────────────────────────
const DRILL_TILES = [
  { label: "Today",        path: "/today" },
  { label: "Command View", path: "/command-view" },
  { label: "Sales Plan",   path: "/sales-plan" },
  { label: "Tower",        path: "/tower" },
  { label: "Helix",        path: "/helix" },
  { label: "Galaxy",       path: "/galaxy" },
];

function DrillDownGrid() {
  const [, setLocation] = useLocation();

  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: MI6_RED,
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        Navigate
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
        }}
      >
        {DRILL_TILES.map((t) => (
          <button
            key={t.label}
            onClick={() => setLocation(t.path)}
            style={{
              background: CARD,
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              padding: "14px 12px",
              fontSize: 13,
              fontWeight: 700,
              color: TEXT_DARK,
              cursor: "pointer",
              textAlign: "center",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.borderColor = MI6_RED)
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.borderColor = BORDER)
            }
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function WorkspacePage() {
  const [, setLocation] = useLocation();
  const session = getSession();
  const role     = (session?.role ?? "ae") as string;
  const userName = session?.name ?? "";

  // Marketing role → redirect to /marketing
  useEffect(() => {
    if (role === "marketing") {
      setLocation("/marketing");
    }
  }, [role, setLocation]);

  const [payload, setPayload] = useState<W50Payload | null>(null);
  const [gapMath, setGapMath] = useState<GapMathData | null>(null);
  const [actStats, setActStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [gapLoading, setGapLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (role === "marketing") return;

    api<W50Payload>("/api/today/w40")
      .then((res) => setPayload(res))
      .catch((e) => setError(e?.message ?? "Could not load workspace data"))
      .finally(() => setLoading(false));

    api<GapMathData>("/api/today/gap-math")
      .then((res) => setGapMath(res))
      .catch(() => setGapMath(null))
      .finally(() => setGapLoading(false));

    // Activity stats endpoint — not yet registered; fall back gracefully
    api<ActivityStats>("/api/workspace/activity-stats")
      .then((res) => setActStats(res))
      .catch(() => setActStats(null)); // deferred — endpoint not yet registered
  }, [role]);

  if (role === "marketing") return null;

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const roleLabel =
    role === "bdr"       ? "BDR"
    : role === "ae"       ? "AE"
    : role === "manager"  ? "Manager"
    : role === "executive"? "Executive"
    : role.toUpperCase();

  const initials = userName
    ? userName.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  return (
    <OperatorShell breadcrumbs={[{ label: "Workspace" }]} showContextTabs={false}>
      <div className="min-h-screen" style={{ background: BG }}>

        {/* Page header */}
        <header
          className="w-full border-b px-6 py-4"
          style={{ background: CARD, borderColor: BORDER }}
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: MI6_RED,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#FFFFFF",
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK }}>
                  {userName || "Operator"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: TEXT_MUTE,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {roleLabel} — Workspace
                </div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: TEXT_MUTE, textAlign: "right" }}>{today}</div>
          </div>
        </header>

        {/* Error banner */}
        {error && (
          <div
            className="mx-6 mt-6 px-4 py-3 rounded border text-sm"
            style={{ borderColor: MI6_RED, color: MI6_RED, background: "#FEF2F2" }}
          >
            {error}
          </div>
        )}

        {/* Page body */}
        <div className="px-4 py-6 space-y-6 max-w-5xl mx-auto pb-16">

          {/* 1. Annual Command Header */}
          <AnnualCommandHeader gapMath={gapMath} loading={gapLoading} />

          {/* 2. Pipeline Coverage Rail */}
          <PipelineCoverageRail gapMath={gapMath} loading={gapLoading} />

          {/* 3. Activity Statistics Strip */}
          <ActivityStrip stats={actStats} loading={loading && actStats === null} />

          {/* 4. Book Health Heat-Map */}
          <BookHealthHeatMap
            accounts={payload?.top_10_accounts ?? []}
            loading={loading}
          />

          {/* 5. Focus This Week */}
          <FocusThisWeek
            accounts={payload?.top_10_accounts ?? []}
            loading={loading}
          />

          {/* 6. Jumpstart Ramp Tile — deferred: hire_date column not yet confirmed in users table.
                When hire_date is available, render a compact tile if within 90 days of onboarding. */}

          {/* 7. Drill-Down Grid */}
          <DrillDownGrid />

        </div>

        {/* Brand footer */}
        <footer
          className="w-full py-4 text-center text-xs border-t"
          style={{ color: TEXT_MUTE, borderColor: BORDER }}
        >
          Attilius — Mi6 Group
        </footer>
        {/* T2 Item 4 — Color Legend Footer (Canon §19) */}
        <ColorLegendFooter />
      </div>
    </OperatorShell>
  );
}
